import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const adminRole = process.env.REACT_APP_ADMIN_ROLE || 'admin';
  const superAdminRole = process.env.REACT_APP_SUPER_ADMIN_ROLE || 'super-admin';

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user_info');
    if (token && storedUser) {
      try {
        const userInfo = JSON.parse(storedUser);
        setUser(userInfo);
        const roles = userInfo.realm_roles || [];
        setUserRoles(roles);
        setIsAdmin(roles.includes(adminRole));
        setIsSuperAdmin(roles.includes(superAdminRole));
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);

      const meResponse = await authAPI.getCurrentUser();
      const userInfo = meResponse.data;

      const roles = userInfo.realm_roles || [];
      const userObj = {
        sub: userInfo.sub,
        username: userInfo.username || email,
        email: userInfo.email || email,
        name: userInfo.name || email,
        groups: userInfo.groups || [],
        realm_roles: roles,
        is_admin: roles.includes(adminRole),
        is_super_admin: roles.includes(superAdminRole),
      };
      localStorage.setItem('user_info', JSON.stringify(userObj));
      setUser(userObj);
      setUserRoles(roles);
      setIsAdmin(roles.includes(adminRole));
      setIsSuperAdmin(roles.includes(superAdminRole));
      setIsAuthenticated(true);

      toast.success(`Вітаємо, ${userObj.name || userObj.username}!`);
      return { success: true };
    } catch (error) {
      // Keycloak reports a pending required action (email not confirmed, or
      // password never chosen) as a plain failed grant, which would otherwise
      // read to the user as a mistyped password.
      const description = error.response?.data?.detail?.error_description || '';
      let message = 'Невірний email або пароль. Спробуйте ще раз.';
      if (description.includes('not fully set up')) {
        message = 'Реєстрацію не завершено. Перевірте пошту та перейдіть за посиланням, щоб підтвердити email і встановити пароль.';
      } else if (description.includes('disabled')) {
        message = 'Обліковий запис заблоковано. Зверніться до адміністратора.';
      }
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (email, firstName, lastName) => {
    try {
      const response = await authAPI.register(email, firstName, lastName);
      const data = response.data;
      if (data.status === 'pending_verification') {
        toast.success(data.message || 'Користувач з таким email вже існує, але реєстрацію не завершено. Лист надіслано повторно.');
        return { success: true, pendingVerification: true, message: data.message };
      }
      toast.success('Реєстрація успішна! Перевірте вашу пошту для підтвердження та встановлення пароля.');
      return { success: true, pendingVerification: false };
    } catch (error) {
      const status = error.response?.status;
      let message = 'Сталася помилка. Спробуйте пізніше.';
      if (status === 409) {
        message = 'Користувач з таким email вже існує. Спробуйте увійти або відновити пароль.';
      } else if (status === 429) {
        message = 'Забагато запитів на цю адресу. Спробуйте пізніше.';
      }
      toast.error(message);
      return { success: false, message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await authAPI.forgotPassword(email);
      // The backend answers the same way for unknown addresses on purpose, so
      // there is nothing here to distinguish – and nothing to leak.
      return { success: true, message: response.data?.message };
    } catch (error) {
      const status = error.response?.status;
      const message = status === 429
        ? 'Забагато запитів на цю адресу. Спробуйте пізніше.'
        : 'Сталася помилка. Спробуйте пізніше.';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
    setUserRoles([]);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setIsAuthenticated(false);
    toast.success('Ви вийшли з системи');
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, register, forgotPassword, logout, userRoles, isAdmin, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};